import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';

import Iconify from '../../../theme/iconify/iconify';
import { questionTypeList } from '../../util/form-values';
import { useResponsive } from '../../../hooks/use-responsive';

function Question({ index, question, answer }) {
    const { t } = useTranslation();
    const mdUp = useResponsive('up', 'md');

    return (
        <Grid item xs={12} key={index}>
            <Stack key={index} sx={{ border: '1px solid rgba(33, 43, 54, 0.12)', borderRadius: '8px' }}>
                <Grid container sx={{ backgroundColor: '#F5F6F6', borderRadius: '8px 8px 0px 0px', p: 0.5, borderBottom: '1px solid rgba(33, 43, 54, 0.12)' }}>
                    <Grid item xs={12}>
                        <Grid container alignItems='center'>
                            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }}>
                                <Typography variant='h6' sx={{ fontSize: '0.9rem !important', textAlign: 'center', ml: 2, mr: 1.25 }}>
                                    {mdUp && `${t('course.modals.newQuiz.questions.question')} ${index + 1}`}
                                    {!mdUp && `${t('course.modals.newQuiz.questions.question')[0]}${index + 1}`}
                                </Typography>
                                <Divider orientation='vertical' flexItem sx={{ my: 0.5, mx: 1 }} />
                                <Tooltip title={t('course.modals.newQuiz.questions.tooltips.questionType')} arrow>
                                    <IconButton
                                        sx={{
                                            borderRadius: '8px',
                                            p: 0.5,
                                            px: 1,
                                            '&:hover': {
                                                backgroundColor: 'rgba(145, 158, 171, 0.08)',
                                            },
                                        }}
                                        disableFocusRipple
                                        disableRipple
                                    >
                                        <Iconify icon={questionTypeList.filter((qt) => qt.type === question?.type)[0].icon} width={18} sx={{ color: 'primary.main' }} />
                                        <Typography variant='body2' sx={{ ml: 0.5, color: 'text.primary', fontSize: '0.8rem' }}>
                                            {t(`course.modals.newQuiz.questions.type.${question?.type}`)}
                                        </Typography>
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                            {mdUp &&
                                <Grid item md={6} sx={{ display: 'flex', justifyContent: 'right', p: 1 }}>
                                    <IconButton
                                        disabled
                                        sx={{
                                            pt: '4px',
                                            pb: '4px',
                                            pr: '4px',
                                            pl: '4px',
                                            borderRadius: '8px',
                                            visibility: 'hidden'
                                        }}
                                    >
                                        <Iconify icon='octicon:move-to-start-16' />
                                    </IconButton>
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container flexDirection='column' justifyContent='flex-start' sx={{ p: '16px', backgroundColor: '#FFF', borderRadius: '0px 0px 16px 16px' }}>
                    <Stack spacing={1}>
                        <Typography variant='body1' sx={{ fontSize: '0.975rem' }}>
                            {question.question}
                        </Typography>
                    </Stack>
                    <Stack spacing={1} sx={{
                        mt: 1,
                        pl: {
                            md: 2,
                            xs: 0
                        }
                    }}>
                        {question.type === 'singleChoice' &&
                            <FormControl>
                                <RadioGroup
                                    value={answer[0]?.id}
                                >
                                    {question.answers.map((a, index) => (
                                        <div key={`${a.id}_${index}`}>
                                            <FormControlLabel
                                                key={index}
                                                value={a.id}
                                                label={a.text}
                                                control={
                                                    <Radio disabled={true} size='small' sx={{
                                                        color: 'primary.main',
                                                        '&.Mui-checked': {
                                                            color: a.is_correct ? 'primary.main' : 'error.main'
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }} />
                                                }
                                                sx={{ '& .MuiTypography-root': { fontSize: '0.975rem' } }}
                                            />
                                        </div>
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        }
                        {question.type === 'multipleChoice' &&
                            <FormGroup>
                                {question.answers.map((a, index) => (
                                    <div key={`${a.id}_${index}`}>
                                        <FormControlLabel
                                            value={a.id}
                                            label={a.text}
                                            checked={answer.some(ans => ans?.id === a.id) || false}
                                            control={
                                                <Checkbox
                                                    size='small'
                                                    disabled={true}
                                                    sx={{
                                                        color: 'primary.main',
                                                        '&.Mui-checked': {
                                                            color: a.is_correct ? 'primary.main' : 'error.main'
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }}
                                                />
                                            }
                                            sx={{ '& .MuiTypography-root': { fontSize: '0.975rem' } }}
                                        />
                                    </div>
                                ))}
                            </FormGroup>
                        }
                    </Stack>
                </Grid>
            </Stack>
        </Grid>
    );
}

export default Question;